// Funciones auxiliares reutilizables
// Estas funciones se pueden usar en cualquier parte de la aplicación

/**
 * Formatea un número como moneda en pesos argentinos
 * @param {number} amount - Cantidad a formatear
 * @returns {string} - String formateado como moneda (ej: "$89.990")
 */
export const formatCurrency = (amount) => {
  if (typeof amount !== 'number') {
    return '$0'
  }
  // Formatear con separador de miles
  return new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount)
}

/**
 * Formatea una fecha a formato legible
 * @param {Date|string|number|Timestamp} date - Fecha a formatear (puede ser Date, string, number o Timestamp de Firestore)
 * @returns {string} - Fecha formateada (ej: "15/12/2024")
 */
export const formatDate = (date) => {
  if (!date) return ''
  
  let dateObj
  
  // Manejar Timestamp de Firestore
  if (date && typeof date === 'object' && 'toDate' in date && typeof date.toDate === 'function') {
    dateObj = date.toDate()
  } else if (date instanceof Date) {
    dateObj = date
  } else {
    dateObj = new Date(date)
  }
  
  if (isNaN(dateObj.getTime())) {
    return ''
  }
  
  return new Intl.DateTimeFormat('es-AR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  }).format(dateObj)
}

/**
 * Trunca un texto a una longitud máxima
 * @param {string} text - Texto a truncar
 * @param {number} maxLength - Longitud máxima
 * @returns {string} - Texto truncado con "..." si es necesario
 */
export const truncateText = (text, maxLength = 100) => {
  if (!text || text.length <= maxLength) {
    return text
  }
  return text.substring(0, maxLength) + '...'
}

/**
 * Valida si un email es válido
 * @param {string} email - Email a validar
 * @returns {boolean} - true si es válido, false si no
 */
export const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(email)
}

/**
 * Obtiene un valor del localStorage de forma segura
 * @param {string} key - Clave del localStorage
 * @param {any} defaultValue - Valor por defecto si no existe
 * @returns {any} - Valor parseado o valor por defecto
 */
export const getFromLocalStorage = (key, defaultValue = null) => {
  try {
    const item = window.localStorage.getItem(key)
    return item ? JSON.parse(item) : defaultValue
  } catch (error) {
    console.error(`Error leyendo localStorage key "${key}":`, error)
    return defaultValue
  }
}

/**
 * Guarda un valor en el localStorage de forma segura
 * @param {string} key - Clave del localStorage
 * @param {any} value - Valor a guardar
 */
export const setToLocalStorage = (key, value) => {
  try {
    window.localStorage.setItem(key, JSON.stringify(value))
  } catch (error) {
    console.error(`Error guardando en localStorage key "${key}":`, error)
  }
}

/**
 * Elimina un valor del localStorage
 * @param {string} key - Clave del localStorage
 */
export const removeFromLocalStorage = (key) => {
  try {
    window.localStorage.removeItem(key)
  } catch (error) {
    console.error(`Error eliminando localStorage key "${key}":`, error)
  }
}

/**
 * Formatea un número para mostrarlo en un input de moneda (con $)
 * @param {number|string} value - Valor numérico
 * @param {boolean} withSeparators - Si true, agrega separadores de miles (default: false para edición fluida)
 * @returns {string} - String formateado con $ (ej: "$107000" o "$107000.50" o "$107.000,50")
 */
export const formatCurrencyInput = (value, withSeparators = false) => {
  if (value === '' || value === null || value === undefined) return ''
  
  // Si es string, puede tener formato ya aplicado o ser un número sin formatear
  let numValue
  if (typeof value === 'string') {
    // Si ya tiene $, parsearlo primero
    if (value.startsWith('$')) {
      numValue = parseCurrencyInput(value)
    } else {
      // Es un número como string, convertir a número
      numValue = parseFloat(value.replace(/[^0-9.,-]/g, '').replace(',', '.'))
    }
  } else {
    numValue = value
  }
  
  if (isNaN(numValue)) return ''
  
  // Convertir a string y verificar si tiene decimales
  const strValue = numValue.toString()
  const parts = strValue.split('.')
  
  if (parts.length === 2 && parts[1]) {
    // Tiene decimales - SIEMPRE usar coma como separador decimal
    const decimals = parts[1].substring(0, 2)
    if (withSeparators) {
      // Formato argentino con separadores de miles: "$107.000,50"
      const integerPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.')
      return `$${integerPart},${decimals}`
    } else {
      // Sin separadores para facilitar edición, pero con coma decimal: "$107000,50"
      return `$${parts[0]},${decimals}`
    }
  }
  
  // Sin decimales
  if (withSeparators) {
    // Con separadores de miles: "$107.000"
    const integerPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.')
    return `$${integerPart}`
  } else {
    // Sin separadores: "$107000"
    return `$${parts[0]}`
  }
}

/**
 * Convierte un valor formateado de moneda a número
 * @param {string} value - Valor formateado (ej: "$107000" o "$107.000,50" o "$107000.50" o "107000,50")
 * @returns {number} - Número extraído
 */
export const parseCurrencyInput = (value) => {
  if (!value) return 0
  
  // Remover $ y espacios
  let cleaned = value.toString().replace(/[$\s]/g, '')
  
  // Detectar si usa punto o coma para decimales
  const hasComma = cleaned.includes(',')
  const hasDot = cleaned.includes('.')
  
  if (hasComma && hasDot) {
    // Formato argentino: punto para miles, coma para decimales (ej: "107.000,50")
    // Remover puntos (separadores de miles) y reemplazar coma por punto para decimales
    cleaned = cleaned.replace(/\./g, '').replace(',', '.')
  } else if (hasComma && !hasDot) {
    // Solo coma: puede ser decimal (ej: "107000,50") o separador de miles (ej: "107,000")
    // Si la coma tiene 1-2 dígitos después, es decimal
    const parts = cleaned.split(',')
    const lastPart = parts[parts.length - 1]
    if (lastPart.length <= 2 && parts.length === 2) {
      // Es decimal
      cleaned = cleaned.replace(',', '.')
    } else {
      // Es separador de miles, remover
      cleaned = cleaned.replace(/,/g, '')
    }
  } else if (hasDot && !hasComma) {
    // Solo punto: puede ser decimal o separador de miles
    const parts = cleaned.split('.')
    const lastPart = parts[parts.length - 1]
    
    // Si hay múltiples puntos, son separadores de miles
    if (parts.length > 2) {
      cleaned = cleaned.replace(/\./g, '')
    } 
    // Si el último segmento tiene 1-2 dígitos, es decimal
    else if (lastPart.length <= 2 && parts.length === 2) {
      // Es decimal, dejarlo como está (parseFloat lo manejará)
    }
    // Si el último segmento tiene exactamente 3 dígitos, puede ser separador de miles o decimal
    // Si el número total tiene más de 6 dígitos, asumir separador de miles
    else if (lastPart.length === 3 && parts.length === 2) {
      const digitsOnly = cleaned.replace(/\./g, '')
      if (digitsOnly.length > 6) {
        // Probablemente separador de miles
        cleaned = cleaned.replace(/\./g, '')
      }
      // Si no, tratarlo como decimal (el usuario puede estar escribiendo)
    }
    // Más de 3 dígitos después del punto: separador de miles
    else if (lastPart.length > 3) {
      cleaned = cleaned.replace(/\./g, '')
    }
  }
  // Si no tiene ni punto ni coma, es un número entero
  
  const parsed = parseFloat(cleaned)
  return isNaN(parsed) ? 0 : parsed
}

/**
 * Obtiene el valor numérico de un input de moneda mientras se escribe
 * @param {string} value - Valor del input
 * @returns {number} - Número extraído
 */
export const getNumericValue = (value) => {
  if (!value) return 0
  // Remover todo excepto números y punto decimal
  const cleaned = value.toString().replace(/[^0-9.]/g, '')
  const parsed = parseFloat(cleaned)
  return isNaN(parsed) ? 0 : parsed
}

/**
 * Calcula el precio final de un producto incluyendo costos asociados
 * @param {Object} product - Objeto del producto
 * @param {Object} category - Objeto de la categoría (opcional, para obtener costos heredados)
 * @returns {number} - Precio final calculado
 */
export const calculateProductFinalPrice = (product, category = null) => {
  const basePrice = product.price || 0
  
  // Sumar costos heredados no excluidos
  let inheritedCostsTotal = 0
  if (category?.preparationCosts && product.inheritedCosts) {
    inheritedCostsTotal = category.preparationCosts.reduce((sum, cost) => {
      const inherited = product.inheritedCosts.find(ic => ic.costId === (cost.id || cost.name))
      if (inherited && !inherited.excluded) {
        return sum + (cost.amount || 0)
      }
      return sum
    }, 0)
  }
  
  // Sumar costos específicos
  const specificCostsTotal = (product.specificCosts || []).reduce((sum, cost) => {
    return sum + (cost.amount || 0)
  }, 0)
  
  const finalPrice = basePrice + inheritedCostsTotal + specificCostsTotal
  
  // Si hay precio especial, retornarlo (solo afecta al precio final, no a los costos)
  if (product.specialPrice && product.specialPrice > 0) {
    return product.specialPrice
  }
  
  return finalPrice
}

export const geocodeAddress = async (addressString) => {
  const q = String(addressString || '').trim()
  if (!q) return null
  const cacheKey = `pinohome_geocode_cache:${q.toLowerCase()}`
  try {
    const cachedRaw = localStorage.getItem(cacheKey)
    if (cachedRaw) {
      const cached = JSON.parse(cachedRaw)
      if (cached && cached.lat && cached.lng) return cached
    }
  } catch {
    // noop
  }
  try {
    const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(q)}`
    const runOnce = async () => {
      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), 9000)
      try {
        const resp = await fetch(url, { headers: { accept: 'application/json' }, signal: controller.signal })
        if (!resp.ok) return null
        const data = await resp.json()
        const best = Array.isArray(data) && data.length ? data[0] : null
        if (!best) return null
        const out = {
          lat: parseFloat(best.lat),
          lng: parseFloat(best.lon),
          displayName: String(best.display_name || q)
        }
        if (!Number.isFinite(out.lat) || !Number.isFinite(out.lng)) return null
        try {
          localStorage.setItem(cacheKey, JSON.stringify(out))
        } catch {
          // noop
        }
        return out
      } finally {
        clearTimeout(timeoutId)
      }
    }

    const first = await runOnce()
    if (first) return first
    await new Promise((r) => setTimeout(r, 350))
    return await runOnce()
  } catch {
    return null
  }
}

export const haversineKm = (a, b) => {
  const toRad = (v) => (v * Math.PI) / 180
  const lat1 = Number(a?.lat)
  const lon1 = Number(a?.lng)
  const lat2 = Number(b?.lat)
  const lon2 = Number(b?.lng)
  if (!Number.isFinite(lat1) || !Number.isFinite(lon1) || !Number.isFinite(lat2) || !Number.isFinite(lon2)) {
    return null
  }
  const R = 6371
  const dLat = toRad(lat2 - lat1)
  const dLon = toRad(lon2 - lon1)
  const s1 = Math.sin(dLat / 2) ** 2
  const s2 = Math.sin(dLon / 2) ** 2
  const c = 2 * Math.asin(Math.sqrt(s1 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * s2))
  return Math.round(R * c * 100) / 100
}

export const normalizeCountryName = (country) => {
  const raw = String(country || '').trim()
  if (!raw) return ''
  const normalized = raw.toLowerCase()
  if (normalized === 'ar' || normalized === 'arg' || normalized === 'argentina' || normalized === 'república argentina' || normalized === 'republica argentina') {
    return 'ARGENTINA'
  }
  if (normalized === 'cl' || normalized === 'chl' || normalized === 'chile') {
    return 'CHILE'
  }
  if (normalized === 'us' || normalized === 'usa' || normalized === 'united states' || normalized === 'estados unidos') {
    return 'ESTADOS UNIDOS'
  }
  if (normalized === 'mx' || normalized === 'mex' || normalized === 'mexico' || normalized === 'méxico') {
    return 'MÉXICO'
  }
  if (normalized === 'es' || normalized === 'esp' || normalized === 'spain' || normalized === 'españa') {
    return 'ESPAÑA'
  }
  return raw.toUpperCase()
}

export const parseNumberLoose = (value) => {
  if (value === null || value === undefined) return null
  if (typeof value === 'number') return Number.isFinite(value) ? value : null
  const raw = String(value).trim()
  if (!raw) return null
  const normalized = raw.replace(/\s+/g, '').replace(',', '.')
  const out = parseFloat(normalized)
  return Number.isFinite(out) ? out : null
}

export const buildAddressQuery = (address) => {
  if (!address || typeof address !== 'object') return ''
  const parts = [
    address.street,
    address.apartment,
    address.city,
    address.postalCode,
    address.province,
    normalizeCountryName(address.country)
  ]
    .map((p) => (typeof p === 'string' ? p.trim() : ''))
    .filter(Boolean)
  return parts.join(', ')
}

export const normalizeCoordinates = (coords) => {
  const lat = Number(coords?.lat)
  const lng = Number(coords?.lng)
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null
  return { lat, lng }
}

